<script lang="ts" setup>
import { computed, nextTick, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router/composables'
import { v4 as uuid } from 'uuid'
import FlipnovelCenteredLoading from '@/components/FlipnovelCenteredLoading.vue'
import { theme as brandTheme } from '@/config/brands'

const props = defineProps<{
  config: any
  input: string
  skeleton: boolean
  fetching: boolean
  ending: boolean
  error: any
  passcode: any
  result: any[]
  setInfiniteRef: (el: HTMLElement | null) => void
  onClear: () => void
  onSearch: () => void
  gotoBack: () => void
  gotoPasscodeBook: (passcode: any) => void
  onTrackGotoBook: (bookId: string) => boolean
}>()
const emit = defineEmits<{
  (event: 'update:input', value: string): void
}>()
const route = useRoute()
const inputRef = ref<HTMLInputElement>()
const pageBgColor = brandTheme.bgGlobal || '#14181F'
const cardPadding = computed(() => props.config.colors.cardPadding || '10px')
const cardBorder = computed(() => props.config.colors.cardBorder || '1px solid rgba(255, 255, 255, 0.06)')
const cardBg = computed(() => props.config.colors.cardBg || 'rgba(255, 255, 255, 0.02)')

function onInput(event: Event) {
  emit('update:input', (event.target as HTMLInputElement).value)
}

onMounted(async () => {
  if (route.query.autofocus === '1' || !route.query.kw) {
    await nextTick()
    inputRef.value?.focus()
  }
})
</script>

<template>
  <div class="rr-search-page h-100dvh overflow-hidden flex flex-col" :style="{ background: pageBgColor }">
    <div class="rr-search-toolbar px-15px pt-14px pb-0">
      <div class="w-full flex items-center space-x-6">
        <div class="relative flex-1 flex items-center">
          <i-assets-icon-search class="rr-search-icon w-4 h-4 absolute left-3 text-n-text-3" />
          <input
            ref="inputRef"
            :value="input"
            enterkeyhint="search"
            class="rr-search-input ml0! flex-1 h-10 pl-9 pr-8 border border-solid border-white/8 !outline-0 truncate caret-n-primary text-n-text-1"
            :style="{ backgroundColor: config.colors.inputBg }"
            :placeholder="config.texts.placeholder"
            autofocus
            @input="onInput"
            @keyup.enter="onSearch"
          >
          <div v-show="input.length" class="absolute right-3 top-2.5" @click="onClear">
            <i-assets-icon-close class="w-4 h-4 text-n-text-1" />
          </div>
        </div>
        <span class="text-14px text-n-primary font-bold" @click="gotoBack()">
          {{ config.texts.cancel }}
        </span>
      </div>
    </div>

    <div :ref="setInfiniteRef" class="rr-search-content flex-1 overflow-y-auto px-15px pb-10px">
      <FlipnovelCenteredLoading v-if="skeleton" />
      <div v-else-if="error" class="mt-4">
        <BizBlockError :title="error.msg" />
      </div>
      <div v-else-if="!passcode && !result.length">
        <BizBlockEmpty :title="config.texts.noResults" />
      </div>
      <template v-else>
        <div v-if="passcode" class="rr-search-list-card">
          <div class="mt-15px flex items-center space-x-15px">
            <img class="w-18 h-25 rounded-8px" :src="passcode.contentCoverUrl" alt="book">
            <div class="min-w-0 flex-1">
              <p class="text-15px font-bold line-clamp-1 text-n-text-1">
                {{ passcode.contentName }}
              </p>
              <p class="text-13px mt-8px text-n-text-2 line-clamp-2">
                {{ passcode.introduce }}
              </p>
              <a
                class="inline-block mt-2 px-12px py-5px text-12px lh-17px rounded-full btn-solid"
                @click="gotoPasscodeBook(passcode)"
              >
                {{ config.texts.startReading }}
              </a>
            </div>
          </div>
        </div>

        <div v-if="result.length" class="-mb-5px">
          <div v-for="item in result" :key="item.id">
            <router-link
              v-slot="{ navigate }"
              custom
              :to="{ name: 'book', params: { contentId: item.id } }"
            >
              <div class="mt-12px rr-search-list-card" @click="onTrackGotoBook(item.id) && navigate($event)">
                <div class="flex items-center space-x-12px">
                  <BizBookCover
                    :id="uuid()"
                    :src="item.contentCoverUrl || ''"
                    :extra="{
                      author: item.authorInfo.author,
                      ...item,
                    }"
                    class="w-18 rounded-8px"
                  />
                  <div class="min-w-0 flex-1">
                    <p class="text-15px font-bold line-clamp-1 text-n-text-1">
                      {{ item.contentName }}
                    </p>
                    <p class="text-13px mt-8px text-n-text-2 line-clamp-2">
                      {{ item.introduce }}
                    </p>
                    <div class="mt-9px flex justify-between text-12px text-n-text-3">
                      <p>
                        <span>{{ item.finishStatus ? config.texts.completed : config.texts.ongoing }}</span>
                        <span v-if="item.tagList[0]"> | </span>
                        <span>{{ item.tagList[0]?.name }}</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </router-link>
          </div>
        </div>

        <div v-if="ending" class="py-15px text-13px text-n-text-3 text-center">
          {{ config.texts.noMore }}
        </div>
        <div v-else-if="fetching" class="py-15px flex items-center justify-center text-13px text-n-text-3">
          <div class="animate-spin">
            <i-icon-park-outline-loading />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.rr-search-page {
  background: v-bind(pageBgColor) !important;
}

.rr-search-toolbar {
  position: sticky;
  top: 0;
  z-index: 10;
  flex-shrink: 0;
  background: transparent;
}

.rr-search-content {
  min-height: 0;
}

.rr-search-input {
  border-radius: 999px !important;
  font-size: 14px;
}

.rr-search-icon {
  top: calc(50% + 1px);
  transform: translateY(-50%);
}

.rr-search-list-card {
  border-radius: 14px;
  border: v-bind(cardBorder);
  padding: v-bind(cardPadding);
  background: v-bind(cardBg);
}
</style>
